/**
 * Ladder engine — the single source of truth for every number shown in a basket.
 * Pure: no IO, no Date.now(). Used by the client (live recalculation) and the server
 * (alerts, attention list, journal messages, close summary).
 *
 * Conventions:
 *  - all prices in USD, quantities in tokens, fee as percent of notional per side;
 *  - buy rung index is 0-based internally, 1-based only in labels ("K1");
 *  - a real fill always beats the plan: realized average (fills) is the TP basis when it exists.
 */

export type Spacing = "linear" | "geometric";
export type Distribution = "equal" | "rising";
export type ExitMode = "single" | "ladder";
export type TpTemplate = "conservative" | "freeride" | "allin" | "manual";
export type BuyFillStatus = "pending" | "filled" | "skipped";

export type TpLevel = { readonly m: number; readonly pct: number };

export type BuyFillInput = {
  readonly status: Exclude<BuyFillStatus, "pending">;
  readonly price: number | null;
  readonly qty: number | null;
};

export type SellFillInput = { readonly price: number; readonly qty: number };

export type LadderInput = {
  readonly budgetUsd: number;
  readonly startPrice: number;
  readonly floor: number;
  readonly rungCount: number;
  readonly spacing: Spacing;
  readonly dist: Distribution;
  readonly feePct: number;
  readonly exitMode: ExitMode;
  readonly target: number;
  readonly tp: readonly TpLevel[];
  readonly moonPct: number;
  /** keyed by 0-based rung index */
  readonly buyFills: Readonly<Record<number, BuyFillInput>>;
  /** keyed by 0-based TP index */
  readonly sellFills: Readonly<Record<number, SellFillInput>>;
};

export type BuyRung = {
  readonly index: number; // 1-based, for labels
  readonly price: number;
  readonly amount: number;
  readonly tokens: number;
  readonly status: BuyFillStatus;
  readonly fillPrice: number | null;
  readonly fillQty: number | null;
};

export type SellRung = {
  readonly index: number; // 1-based
  readonly mult: number;
  readonly pct: number;
  readonly price: number;
  readonly qty: number;
  readonly revenue: number;
  readonly profit: number;
  readonly executed: boolean;
  readonly fillPrice: number | null;
  readonly fillQty: number | null;
};

export type LadderResult = {
  readonly buys: readonly BuyRung[];
  readonly sells: readonly SellRung[];
  readonly planTokens: number;
  readonly planAvg: number;
  readonly realAvg: number | null;
  /** TP basis: realized average when any fill exists, else plan average */
  readonly baseAvg: number;
  readonly spent: number;
  /** spent including the buy-side fee — the cost side of every profit figure */
  readonly costBasis: number;
  readonly held: number;
  /** tokens still held after executed TPs */
  readonly heldNow: number;
  readonly breakEven: number;
  readonly exitTokens: number;
  readonly totalProfit: number;
  readonly realizedRevenue: number;
  readonly realizedProfit: number;
  readonly moonQty: number;
  readonly moonValue: number;
  readonly remaining: number;
  readonly pctSum: number;
  readonly filledCount: number;
  readonly skippedCount: number;
};

export const TP_TEMPLATES: Record<
  Exclude<TpTemplate, "manual">,
  (target: number, moonPct: number) => TpLevel[]
> = {
  conservative: () => [
    { m: 1.5, pct: 40 },
    { m: 2, pct: 35 },
    { m: 3, pct: 25 },
  ],
  freeride: () => [
    { m: 2, pct: 30 },
    { m: 3, pct: 25 },
    { m: 5, pct: 35 },
  ],
  allin: (target, moonPct) => [{ m: target, pct: 100 - moonPct }],
};

export function clampRungCount(n: number): number {
  return Math.min(12, Math.max(2, Math.round(n)));
}

/** Planned rung prices from start down to floor (inclusive). */
export function rungPrices(startPrice: number, floor: number, rungCount: number, spacing: Spacing): number[] {
  const n = clampRungCount(rungCount);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(
      spacing === "geometric"
        ? startPrice * (floor / startPrice) ** (i / (n - 1))
        : startPrice - (i * (startPrice - floor)) / (n - 1),
    );
  }
  return out;
}

/** Geometric step between rungs as a percent, e.g. 12.3 (%). */
export function geometricStepPct(startPrice: number, floor: number, rungCount: number): number {
  const n = clampRungCount(rungCount);
  if (startPrice <= 0 || floor <= 0) return 0;
  return (1 - (floor / startPrice) ** (1 / (n - 1))) * 100;
}

export function effectiveTp(input: Pick<LadderInput, "exitMode" | "target" | "tp" | "moonPct">): TpLevel[] {
  return input.exitMode === "single" ? [{ m: input.target, pct: 100 - input.moonPct }] : [...input.tp];
}

export function calcLadder(b: LadderInput): LadderResult {
  const n = clampRungCount(b.rungCount);
  const fee = b.feePct / 100;
  const prices = rungPrices(b.startPrice, b.floor, n, b.spacing);
  const weights = prices.map((_, i) => (b.dist === "rising" ? i + 2 : 1));
  const sw = weights.reduce((a, c) => a + c, 0);

  const buys: BuyRung[] = prices.map((p, i) => {
    const amount = (b.budgetUsd * (weights[i] ?? 1)) / sw;
    const f = b.buyFills[i];
    return {
      index: i + 1,
      price: p,
      amount,
      tokens: p > 0 ? amount / p : 0,
      status: f?.status ?? "pending",
      fillPrice: f?.status === "filled" ? (f.price ?? p) : null,
      fillQty: f?.status === "filled" ? (f.qty ?? (p > 0 ? amount / p : 0)) : null,
    };
  });

  const planTokens = buys.reduce((a, r) => a + r.tokens, 0);
  const planAvg = planTokens > 0 ? b.budgetUsd / planTokens : b.startPrice;
  const filled = buys.filter((r) => r.status === "filled");
  const spent = filled.reduce((a, r) => a + (r.fillPrice ?? 0) * (r.fillQty ?? 0), 0);
  const held = filled.reduce((a, r) => a + (r.fillQty ?? 0), 0);
  const realAvg = held > 0 ? spent / held : null;
  const baseAvg = realAvg ?? planAvg;
  // buy fee paid on the way in, sell fee on the way out
  const breakEven = fee < 1 ? (baseAvg * (1 + fee)) / (1 - fee) : baseAvg;
  const exitTokens = held > 0 ? held : planTokens;

  const tp = effectiveTp(b);
  const sells: SellRung[] = tp.map((x, i) => {
    const sf = b.sellFills[i];
    const planPrice = baseAvg * x.m;
    const planQty = (exitTokens * x.pct) / 100;
    const priceUsed = sf ? sf.price : planPrice;
    const qtyUsed = sf ? sf.qty : planQty;
    const revenue = qtyUsed * priceUsed * (1 - fee);
    const cost = qtyUsed * baseAvg * (1 + fee);
    return {
      index: i + 1,
      mult: x.m,
      pct: x.pct,
      price: planPrice,
      qty: planQty,
      revenue,
      profit: revenue - cost,
      executed: !!sf,
      fillPrice: sf ? sf.price : null,
      fillQty: sf ? sf.qty : null,
    };
  });

  const soldQty = sells.filter((s) => s.executed).reduce((a, s) => a + (s.fillQty ?? 0), 0);
  const realizedRevenue = sells.filter((s) => s.executed).reduce((a, s) => a + s.revenue, 0);
  const realizedProfit = sells.filter((s) => s.executed).reduce((a, s) => a + s.profit, 0);
  const moonQty = (exitTokens * b.moonPct) / 100;
  const lastPrice = sells.length ? (sells[sells.length - 1]?.price ?? baseAvg) : baseAvg;

  return {
    buys,
    sells,
    planTokens,
    planAvg,
    realAvg,
    baseAvg,
    spent,
    costBasis: spent * (1 + fee),
    held,
    heldNow: Math.max(0, held - soldQty),
    breakEven,
    exitTokens,
    totalProfit: sells.reduce((a, s) => a + s.profit, 0),
    realizedRevenue,
    realizedProfit,
    moonQty,
    moonValue: moonQty * lastPrice,
    remaining: b.budgetUsd - spent,
    pctSum: tp.reduce((a, x) => a + x.pct, 0) + b.moonPct,
    filledCount: filled.length,
    skippedCount: buys.filter((r) => r.status === "skipped").length,
  };
}

/** Percent distance from the current price to TP1, negative when TP1 is already below the market. */
export function distanceToTp1(result: LadderResult, currentPrice: number | null): number | null {
  const tp1 = result.sells[0];
  if (!tp1 || currentPrice == null || currentPrice <= 0) return null;
  return ((tp1.price - currentPrice) / currentPrice) * 100;
}

export type BasketLifecycle = "waiting" | "active" | "closed";

export type BasketStatusKey = "czeka" | "aktywny" | "rewizja" | "zamkniety";

export type BasketStatus = {
  readonly key: BasketStatusKey;
  readonly label: string;
  /** hex color for edges / chips */
  readonly color: string;
};

export function basketStatus(
  b: { readonly status: BasketLifecycle; readonly floor: number },
  currentPrice: number | null,
): BasketStatus {
  if (b.status === "closed") return { key: "zamkniety", label: "zamknięty", color: "#9CA3AF" };
  if (b.status === "waiting") return { key: "czeka", label: "czeka na warunek startu", color: "#9CA3AF" };
  if (currentPrice != null && currentPrice < b.floor)
    return { key: "rewizja", label: "podłoga przebita · teza do rewizji", color: "#F05252" };
  return { key: "aktywny", label: "DCA w trakcie", color: "#E3A008" };
}

export function isFloorBroken(
  b: { readonly status: BasketLifecycle; readonly floor: number },
  currentPrice: number | null,
): boolean {
  return b.status === "active" && currentPrice != null && currentPrice < b.floor;
}

/** Next pending rung, or null when everything is filled/skipped. */
export function nextPendingRung(result: LadderResult): BuyRung | null {
  return result.buys.find((r) => r.status === "pending") ?? null;
}

export type CloseSummary = {
  readonly resultUsd: number;
  readonly resultPct: number | null;
  readonly days: number;
  readonly buyFilled: number;
  readonly buyTotal: number;
  readonly tpHit: readonly number[]; // 1-based TP indices
  readonly tpTotal: number;
  /** share of buy rungs filled within ±5 % of plan price */
  readonly adherencePct: number;
  readonly heldAtClose: number;
  readonly markPrice: number | null;
};

export function closeSummary(
  result: LadderResult,
  opts: { readonly startedAt: Date | null; readonly closedAt: Date; readonly markPrice: number | null },
): CloseSummary {
  const filled = result.buys.filter((r) => r.status === "filled");
  const onPlan = filled.filter(
    (r) => r.fillPrice != null && r.price > 0 && Math.abs(r.fillPrice / r.price - 1) <= 0.05,
  ).length;
  const markValue = opts.markPrice != null ? result.heldNow * opts.markPrice : 0;
  const resultUsd = result.realizedRevenue + markValue - result.costBasis;
  const days = opts.startedAt
    ? Math.max(0, Math.round((opts.closedAt.getTime() - opts.startedAt.getTime()) / 86_400_000))
    : 0;
  return {
    resultUsd,
    resultPct: result.costBasis > 0 ? (resultUsd / result.costBasis) * 100 : null,
    days,
    buyFilled: filled.length,
    buyTotal: result.buys.length,
    tpHit: result.sells.filter((s) => s.executed).map((s) => s.index),
    tpTotal: result.sells.length,
    adherencePct: result.buys.length ? Math.round((onPlan / result.buys.length) * 100) : 0,
    heldAtClose: result.heldNow,
    markPrice: opts.markPrice,
  };
}
